// This file is kept for any shared types across the IDP module

/**
 * Client-side field location format
 * Used by UI components for positioning annotations
 */
export interface IIDPFieldLocation {
  top: number;
  bottom: number;
  left: number;
  right: number;
  page_index: number;
  /**
   * When true, `top`/`bottom`/`left`/`right` are ABSOLUTE pixel coordinates in
   * the document's native space rather than normalized 0..1 fractions. The page
   * renderer divides them by the page's native size to recover fractions before
   * drawing (see `normalizePixelSpaceFields`). The legacy astral flow emits
   * normalized boxes (flag absent/false); astral2/rain emits pixel-space boxes.
   */
  pixel_space?: boolean;
}

/**
 * Backend bounding box format (actual API response)
 * Uses x, y, width, height coordinates (normalized 0-1)
 */
export interface IIDPBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Field type enumeration
 * Maps to element types for consistent rendering
 */
export enum EIDPFieldType {
  STRING = 'string',
  NUMBER = 'number',
  TABLE = 'table',
  KEY_VALUE = 'key_value',
  ENTITY = 'entity',
  FORM = 'form',
  TEXT = 'text',
  LIST = 'list',
  SELECT = 'select',
}

/**
 * IDP document toolbar view mode.
 * Controls annotation and fields panel visibility.
 */
export type TIDPViewMode = 'none' | 'fieldMap' | 'extractedFields';

/**
 * IDP source context enumeration
 */
export enum EIDPSource {
  OUTPUT = 'output',
  EXCEPTION = 'exception',
}

/**
 * Context types for related outputs from Astral.
 */
export type TRelatedOutputsContext =
  | 'need_information'
  | 'analyze_outputs'
  | 'manual_action_required';

/**
 * A single fact with field name, type, status, and optional value.
 */
export interface IIDPFact {
  /** Field name */
  field: string;
  /** Data type (text, number, boolean, table, json, select) */
  type: string;
  /** Extraction status: 'missing' (needs user input) or 'present' (already has value) */
  status: 'missing' | 'present';
  /** Value if present, undefined if missing */
  value?: string;
  /** Options for select type fields (dropdown choices) */
  options?: string[];
  /** Page number (1-indexed) within the document where this fact was extracted */
  page_number?: number;
  /** Extraction confidence score for this fact */
  confidence?: number;
  /** Bounding box for highlighting the fact on the document page */
  bounding_box?: IIDPBoundingBox;
}

/**
 * Source information for a group of facts.
 * Identifies where facts were extracted from.
 */
export interface IIDPFactSource {
  /** Source name (file resource name for documents, descriptive name for external systems) */
  name?: string;
  /** Page number (1-indexed) for document sources */
  page?: number;
}

/**
 * A group of facts from a single source.
 * Groups facts by their origin (document, external system, etc.)
 */
export interface IIDPFactGroup {
  /** Optional source information */
  source?: IIDPFactSource;
  /** Array of facts from this source */
  facts: IIDPFact[];
}

/**
 * A group of IDP fields from a single source.
 * Used by UI components for rendering and editing grouped fields.
 */
export interface IIDPFieldGroup {
  /** Optional source information */
  source?: IIDPFactSource;
  /** Fields in this group */
  fields: IIDPMissingField[];
}

/**
 * IDP fact input data stored for document editing in exception flow.
 * Used to trigger IDP overlay from exception messages.
 *
 * Supports both:
 * - New format with factGroups (grouped by source)
 * - Legacy format with flat facts array (backward compatibility)
 */
export interface IIDPFactInputData {
  /** Context type from the related_outputs (need_information, analyze_outputs, manual_action_required) */
  context?: TRelatedOutputsContext;
  /**
   * Facts organized by source groups (new format).
   * Each group contains facts from a single source with optional source metadata.
   */
  factGroups?: IIDPFactGroup[];
  /**
   * @deprecated Use factGroups instead. Kept for backward compatibility.
   * Flat array of facts without source grouping.
   */
  facts?: IIDPFact[];
  /**
   * @deprecated Use factGroups[].source.name instead. Kept for backward compatibility.
   * Source file/resource the facts were derived from (e.g., file resource name for document download).
   */
  source?: string;
}

/**
 * IDP result type discriminator
 * Used to identify the type of IDP API response.
 * Includes both legacy values and current book-idp contract values.
 */
export enum EIDPResultType {
  /** @deprecated Legacy value — use FIELDS_EXTRACTION */
  EXTRACTION_RESULT = 'extraction_result',
  /** @deprecated Legacy value — use TABLES_EXTRACTION */
  TABLE_EXTRACTION = 'table_extraction',
  /** @deprecated Legacy value — use LAYOUT_PARSING */
  DOCUMENT_ANALYSIS = 'document_analysis',

  /** Result from extract_fields_from_text_or_documents */
  FIELDS_EXTRACTION = 'fields_extraction',
  /** Result from extract_tables_from_text_or_documents */
  TABLES_EXTRACTION = 'tables_extraction',
  /** Result from extract_table_records_from_documents (merged multi-page records table) */
  TABLE_RECORDS_EXTRACTION = 'table_records_extraction',
  /** Result from parse_layout_from_a_document */
  LAYOUT_PARSING = 'layout_parsing',
  /** Result from read_content_from_documents */
  READ_CONTENT = 'read_content',
  /**
   * Result from classify_text_or_documents.
   * Not routed through the IDP rendering pipeline — classification results
   * render as generic dictionaries because they carry no bounding boxes or
   * document-level fields.
   */
  CLASSIFICATION = 'classification',
}

/**
 * IDP element type discriminator
 * Used to identify the type of extracted element within a result.
 * Includes both legacy values and current book-idp contract values.
 */
export enum EIDPElementType {
  /** @deprecated Legacy value — use DOCUMENT_FIELD */
  EXTRACTED_FIELD = 'extracted_field',
  /** @deprecated Legacy value — use DOCUMENT_IDP_TABLE */
  TABLE = 'table',
  /** @deprecated Legacy value — use DOCUMENT_KEY_VALUE_PAIR */
  KEY_VALUE = 'key_value',
  /** @deprecated Legacy entity from analyze_thing */
  ENTITY = 'entity',
  /** @deprecated Legacy form from analyze_thing */
  FORM = 'form',
  /** @deprecated Legacy value — use DOCUMENT_TEXT */
  TEXT = 'text',
  /** @deprecated Legacy list from analyze_thing */
  LIST = 'list',

  /** Field extracted from a document (book-idp) */
  DOCUMENT_FIELD = 'document_field',
  /** Field extracted from text input (book-idp) */
  TEXT_FIELD = 'text_field',
  /** Table extracted from a document with location metadata (book-idp) */
  DOCUMENT_IDP_TABLE = 'document_idp_table',
  /** Table extracted from text input (book-idp) */
  TEXT_IDP_TABLE = 'text_idp_table',
  /** Bare merged records table from table_records_extraction (book-idp); no per-table location */
  IDP_TABLE = 'idp_table',
  /** Key-value pair from layout parsing (book-idp) */
  DOCUMENT_KEY_VALUE_PAIR = 'document_key_value_pair',
  /** Text segment from layout parsing (book-idp) */
  DOCUMENT_TEXT = 'document_text',
}

/**
 * Common properties shared by all IDP elements
 */
export interface IIDPBaseElement {
  /** Element type discriminator */
  element_type: EIDPElementType | string;
  /** Page number (1-based from backend) */
  page_number: number;
  /** Bounding box with x, y, width, height (0-1 normalized) */
  bounding_box: IIDPBoundingBox | null;
  /**
   * Confidence score (0-100), absent when the producer has none. A value that
   * did not come from an extractor (a step's plain return, say) has no
   * confidence, and reporting it as 0 reads as "the model was certain it was
   * wrong" rather than "there is nothing to report".
   */
  confidence?: number;
  /** Whether content is handwritten */
  is_handwritten?: boolean;
  /** Verification result */
  verification?: unknown | null;
  /** Document filename */
  document?: string;
  /** Document index for multi-doc processing */
  document_index?: number | null;
}

/**
 * Extracted field from extract_data_from_thing
 */
export interface IIDPExtractedField extends IIDPBaseElement {
  element_type: typeof EIDPElementType.EXTRACTED_FIELD;
  /** Field name */
  name: string;
  /** Array of extracted values (primitives) */
  values: Array<string | number>;
}

/**
 * Table from analyze_thing or extract_table_from_thing
 */
export interface IIDPTable extends IIDPBaseElement {
  element_type: typeof EIDPElementType.TABLE;
  /** Table title/description */
  title?: string;
  /** Column headers */
  headers: string[];
  /** Table rows */
  rows: string[][];
  /** Number of rows */
  num_rows: number;
  /** Number of columns */
  num_cols: number;
}

/**
 * Key-value pair from analyze_thing
 */
export interface IIDPKeyValue extends IIDPBaseElement {
  element_type: typeof EIDPElementType.KEY_VALUE;
  /** The key */
  key: string;
  /** The value */
  value: string;
}

/**
 * Entity from analyze_thing
 */
export interface IIDPEntity extends IIDPBaseElement {
  element_type: typeof EIDPElementType.ENTITY;
  /** Entity type (person, date, currency, etc.) */
  entity_type: string;
  /** Original text */
  text: string;
  /** Normalized/parsed value */
  normalized_value: string | number;
  /** Surrounding text context */
  context?: string;
  /** Whether the text is handwritten */
  is_handwritten?: boolean;
}

/**
 * Form from analyze_thing
 */
export interface IIDPForm extends IIDPBaseElement {
  element_type: typeof EIDPElementType.FORM;
  /** Form title */
  title?: string;
  /** Form fields */
  fields: Array<{
    label: string;
    value: string;
    field_type: string;
    is_checked?: boolean | null;
    confidence: number;
  }>;
}

/**
 * Text block from analyze_thing
 */
export interface IIDPTextBlock extends IIDPBaseElement {
  element_type: typeof EIDPElementType.TEXT;
  /** Text content */
  content: string;
  /** Style (heading, paragraph) */
  style: 'heading' | 'paragraph';
  /** Heading level (1, 2, 3, etc.) - only for headings */
  level?: number | null;
}

/**
 * List from analyze_thing
 */
export interface IIDPListBlock extends IIDPBaseElement {
  element_type: typeof EIDPElementType.LIST;
  /** List type (bullet, numbered, etc.) */
  list_type: string;
  /** List items */
  items: string[];
}

/**
 * Union type for all IDP field types from backend
 */
export type IIDPField =
  | IIDPExtractedField
  | IIDPTable
  | IIDPKeyValue
  | IIDPEntity
  | IIDPForm
  | IIDPTextBlock
  | IIDPListBlock;

/**
 * Client-side IDP field with unique ID and single value
 */
export interface IIDPClientField {
  /** Unique identifier for the field */
  id: string;
  /** Field name */
  name: string;
  /** Single value (one per client field) */
  value: string;
  /** Confidence score */
  confidence?: number;
  /** Location on the page - can be null when field extraction fails */
  location: IIDPFieldLocation | null;
  /** Document name */
  document: string;
  /** Backend element type (preserved for proper rendering) */
  element_type: EIDPElementType | string;
  /** Field type (inferred from value for input type) */
  type: EIDPFieldType;
}

/**
 * Exception field for exception flow (can be missing or present)
 */
export interface IIDPMissingField {
  /** Field name */
  name: string;
  /** Document name */
  document: string;
  /** Field type (string, number, table, or select) */
  type?: EIDPFieldType;
  /** Optional description or help text */
  description?: string;
  /** Optional value for present fields (fields with status="present") */
  value?: string;
  /** Field status: 'missing' needs user input, 'present' is already extracted */
  status?: 'missing' | 'present';
  /** Options for select type fields (dropdown choices) */
  options?: string[];
  /** Extraction confidence score */
  confidence?: number;
  /** Page number (1-indexed) within the document */
  page_number?: number;
  /** Bounding box for highlighting the field on the document page */
  bounding_box?: IIDPBoundingBox;
}

/**
 * Client-side exception field for exception flow
 */
export interface IIDPClientMissingField {
  /** Unique identifier for the field */
  id: string;
  /** Field name */
  name: string;
  /** Current value (empty initially, filled by user) */
  value: string;
  /** Document name */
  document: string;
  /** Field type (string, number, table, or select) */
  type: EIDPFieldType;
  /** Optional description or help text */
  description?: string;
  /** Field status: 'missing' needs user input, 'present' is already extracted */
  status?: 'missing' | 'present';
  /** Options for select type fields (dropdown choices) */
  options?: string[];
  /** Extraction confidence score */
  confidence?: number;
  /** Location on the page for annotation linking - null when no bounding box */
  location?: IIDPFieldLocation | null;
}
